import { IS_ENCRYPTION_ENABLED } from 'src/config';
import {
  type Builder,
  getHeaders,
  getQueryString
} from 'src/services/utils.ts';

export type InternalAnalytics = {
  count: number;
  name: string;
};

export type CreditConsumptionResponse = {
  data: InternalAnalytics[];
  pagination: {
    pn: number;
    ps: number;
    total_count: number;
    total_pages: number;
  };
};

export type TAnalyticsResponse = {
  chat_counts: { count: number; percent: number | null };
  advance_searches: { count: number; percent: number | null };
  workspaces: { count: number; percent: number | null };
  workflow: { count: number; percent: number | null };
  chat_engagement: {
    conversations: number;
    queries: number;
    timeseries: {
      date?: string;
      hour?: string;
      week?: string;
      conversations: number;
      queries: number;
    }[];
  };
  data_library: { name: string; count: number }[];
  assistants: {
    assistant_id: number;
    assistant_name: string;
    chat_creation_count: number;
    chat_query_count: number;
    old_chat_creation_count: number;
    old_chat_query_count: number;
  }[];
  mini_experts: {
    document_analyst: { count: number; percentage: number | null };
    smart_meet: { count: number; percentage: number | null };
    data_analyst: { count: number; percentage: number | null };
  };
  model_usage: { name: string; usage_count: number }[];
  model_usage_ts: {
    date?: string;
    hour?: string;
    day?: string;
    month?: string;
    year?: string;
    name: string;
    usage_count: number;
  }[];
};

export type TChatBigNumbersResponse = {
  chats: { count: number; percent: number | null };
  chats_per_day: { count: number; percent: number | null };
  active_members: { count: number; percent: number | null };
  query_success_rate: { count: number; percent: number | null };
  tool_usage_ts: {
    date?: string;
    hour?: string;
    day?: string;
    month?: string;
    year?: string;
    web_search_count: number;
    image_generation_count: number;
  }[];
  chat_knowledge_ts: {
    date?: string;
    hour?: string;
    day?: string;
    month?: string;
    year?: string;
    enterprise_count: number;
    world_count: number;
  }[];
  chats_by_modules: {
    chats: { chat_count: number; user_count: number; messages_count: number };
    projects: { chat_count: number; user_count: number; messages_count: number };
    assistants: {
      chat_count: number;
      user_count: number;
      messages_count: number;
    };
    smart_meet: { chat_count: number; user_count: number; messages_count: number };
    more_others: {
      chat_count: number;
      user_count: number;
      messages_count: number;
    };
  };
  model_usage: { name: string; usage_count: number }[];
  model_usage_ts: {
    date?: string;
    hour?: string;
    day?: string;
    month?: string;
    year?: string;
    name: string;
    usage_count: number;
  }[];
};

export type TTenantAnalyticsParams = {
  username?: string;
  start_date: string;
  end_date: string;
  app_id?: string;
  tenant_id?: string;
};

export type TUsersCountsParams = {
  username?: string;
  start_date: string;
  end_date: string;
};

export type TProcessedDocumentsParams = {
  username?: string;
  start_date: string;
  end_date: string;
};

export type TUsageParams = {
  start_date: Date | string;
  end_date: Date | string;
};

export type TUsageResponse = {
  unit: string;
  total: number;
  monthly_usage: number;
  daily_usage: { date: string | Date; consumed: number }[];
  trial_usage?: {
    unit: string;
    total: number;
    consumed: number | null;
    start_date: string | null;
    expiry_date: string | null;
  };
};

export type TProfileStatsParams = {
  username?: string;
};

export type TTenantCreationDateResponse = {
  data: { creation_date: string | null };
};

export type TTenantCreationDateParams = {
  app_id?: string;
  tenant_id?: string;
};

export default (builder: Builder) => {
  return {
    getTenantAnalytics: builder.query<
      TAnalyticsResponse,
      void | TTenantAnalyticsParams
    >({
      async queryFn(arg, api, extraOptions, fetchWithBQ) {
        const queryString = getQueryString(arg);
        let url = `analytics/tenant-analytics?${queryString}`;
        const result: any = await fetchWithBQ({
          url,
          ...getHeaders(url),
          responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
        });
        return result.error ? result : result?.data;
      }
    }),
    getChatBigNumbers: builder.query<
      TChatBigNumbersResponse,
      void | TTenantAnalyticsParams
    >({
      async queryFn(arg, api, extraOptions, fetchWithBQ) {
        const queryString = getQueryString(arg);
        let url = `analytics/chat-big-numbers?${queryString}`;
        const result: any = await fetchWithBQ({
          url,
          ...getHeaders(url),
          responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
        });
        return result.error ? result : result?.data;
      }
    }),
    getUsersCounts: builder.query<
      InternalAnalytics[],
      void | TUsersCountsParams
    >({
      async queryFn(arg, api, extraOptions, fetchWithBQ) {
        const queryString = getQueryString(arg);
        let url = `analytics/users-count?${queryString}`;
        const result: any = await fetchWithBQ({
          url,
          ...getHeaders(url),
          responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
        });
        return result.error ? result : result?.data;
      }
    }),
    getProcessedDocuments: builder.query<
      InternalAnalytics[],
      void | TProcessedDocumentsParams
    >({
      async queryFn(arg, api, extraOptions, fetchWithBQ) {
        const queryString = getQueryString(arg);
        let url = `analytics/processed-documents?${queryString}`;
        const result: any = await fetchWithBQ({
          url,
          ...getHeaders(url),
          responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
        });
        return result.error ? result : result?.data;
      }
    }),
    getUsage: builder.query<TUsageResponse, TUsageParams>({
      async queryFn(arg, api, extraOptions, fetchWithBQ) {
        const queryString = getQueryString(arg);
        let url = `analytics/all-usage?${queryString}`;
        const result: any = await fetchWithBQ({
          url,
          ...getHeaders(url),
          responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
        });
        return result.error ? result : result?.data;
      }
    }),
    getProfileStatsCount: builder.query<
      InternalAnalytics[],
      void | TProfileStatsParams
    >({
      async queryFn(arg, api, extraOptions, fetchWithBQ) {
        const queryString = getQueryString(arg);
        let url = `analytics/profile-counts?${queryString}`;
        const result: any = await fetchWithBQ({
          url,
          ...getHeaders(url),
          responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
        });
        return result.error ? result : result?.data;
      }
    }),
    getCreditConsumptionData: builder.query<CreditConsumptionResponse, any>({
      async queryFn(arg, api, extraOptions, fetchWithBQ) {
        const queryString = getQueryString(arg);
        let url = `analytics/credit-consumption?${queryString}`;
        const result: any = await fetchWithBQ({
          url,
          ...getHeaders(url),
          responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
        });
        return result.error ? result : { data: result.data };
      }
    }),
    getTenantCreationDate: builder.query<
      TTenantCreationDateResponse,
      void | TTenantCreationDateParams
    >({
      async queryFn(arg, api, extraOptions, fetchWithBQ) {
        const queryString = getQueryString(arg);
        let url = `analytics/tenant-creation-date?${queryString}`;
        const result: any = await fetchWithBQ({
          url,
          ...getHeaders(url),
          responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
        });
        return result.error ? result : result?.data;
      }
    }),
    getAllTenants: builder.query({
      async queryFn(arg, api, extraOptions, fetchWithBQ) {
        const queryString = getQueryString(arg.params);
        let url = `analytics/fetch-tenants?${queryString}`;
        const result: any = await fetchWithBQ({
          url,
          ...getHeaders(url),
          responseHandler: IS_ENCRYPTION_ENABLED ? 'text' : 'json'
        });
        return result.error ? result : result?.data;
      }
    })
  };
};
